import { useReducer } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation, Thumbs } from "swiper/modules";
import dayjs from "dayjs";
import ReactPlayer from "react-player";
import "dayjs/locale/pt-br";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

import { Icon, Text } from "@aprosoja/ui";
import { imageLoader } from "@aprosoja/utils";

import "./styles.css";
import {
  MediaGalleryReducerState,
  reducerMediaGallery,
} from "./media-gallery.reducer";
import Tabs from "../tabs/tabs";

type FileType = {
  fileTitle: string;
  highlight: boolean;
  fileUrl: string;
};

type MediaGalleryProps = {
  photos?: { title: string; createdAt: string; file: FileType }[];
  videos?: { title: string; createdAt: string; ytbLink: string }[];
};

const MediaGallery = ({ photos, videos }: MediaGalleryProps) => {
  const [mediaGalleryReducer, setMediaGalleryReducer] = useReducer(
    reducerMediaGallery,
    {
      thumbsSwiper: undefined,
      currentSlide: 0,
      mediaType: "photos",
      isVideoPlaying: false,
    }
  );

  return (
    <div className="flex flex-col gap-size-x4 w-full">
      <div className="flex flex-col gap-size-x4 w-full">
        <Tabs
          value={mediaGalleryReducer.mediaType}
          onChange={(value) =>
            setMediaGalleryReducer({
              type: "mediaType",
              mediaTypePayload: value as MediaGalleryReducerState["mediaType"],
            })
          }
          tabs={[
            { title: "Fotos", value: "photos" },
            { title: "Videos", value: "videos" },
          ]}
        />
        <Swiper
          spaceBetween={10}
          navigation={true}
          thumbs={{ swiper: mediaGalleryReducer.thumbsSwiper }}
          modules={[FreeMode, Navigation, Thumbs]}
          onSlideChange={(v) => {
            setMediaGalleryReducer({
              type: "currentSlide",
              currentSlidePayload: v.activeIndex,
            });
            setMediaGalleryReducer({
              type: "isVideoPlaying",
              isVideoPlayingPayload: false,
            });
          }}
          className="h-[877px] w-full"
        >
          {mediaGalleryReducer.mediaType === "photos"
            ? photos?.map((photo, index) => (
                <SwiperSlide key={`${photo.title}-${index}`}>
                  <Image
                    fill
                    src={photo.file.fileUrl}
                    alt={`Foto ${index}`}
                    loader={imageLoader}
                    className="rounded-border-radius-small"
                    style={{ objectFit: "cover" }}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </SwiperSlide>
              ))
            : videos?.map((video, index) => (
                <SwiperSlide key={`${video.title}-${index}`}>
                  <ReactPlayer
                    playing={mediaGalleryReducer.isVideoPlaying === index}
                    height="100%"
                    width="100%"
                    controls
                    url={video.ytbLink}
                    volume={0.5}
                    onPlay={() =>
                      setMediaGalleryReducer({
                        type: "isVideoPlaying",
                        isVideoPlayingPayload: index,
                      })
                    }
                    onPause={() =>
                      setMediaGalleryReducer({
                        type: "isVideoPlaying",
                        isVideoPlayingPayload: false,
                      })
                    }
                  />
                </SwiperSlide>
              ))}
        </Swiper>
        <Swiper
          onSwiper={(swiper) =>
            setMediaGalleryReducer({
              type: "thumbsSwiper",
              thumbsSwiperPayload: swiper,
            })
          }
          spaceBetween={10}
          slidesPerView={4}
          freeMode={true}
          watchSlidesProgress={true}
          modules={[FreeMode, Navigation, Thumbs]}
          onSlideChange={(v) => {
            setMediaGalleryReducer({
              type: "currentSlide",
              currentSlidePayload: v.activeIndex,
            });
            setMediaGalleryReducer({
              type: "isVideoPlaying",
              isVideoPlayingPayload: false,
            });
          }}
          className="preview h-[240px] w-full opacity"
        >
          {mediaGalleryReducer.mediaType === "photos"
            ? photos?.map((photo, index) => (
                <SwiperSlide
                  key={`${photo.title}-${index}`}
                  className="min-w-[240px] max-w-[240px]"
                >
                  <Image
                    fill
                    src={photo.file.fileUrl}
                    alt={`Foto ${index}`}
                    loader={imageLoader}
                    className="rounded-border-radius-small"
                    style={{ objectFit: "cover" }}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </SwiperSlide>
              ))
            : videos?.map((video, index) => (
                <SwiperSlide
                  key={`${video.title}-${index}`}
                  className="min-w-[240px] max-w-[240px] bg-th-color-neutral-500 rounded-border-radius-small"
                >
                  <div className="flex w-full h-full justify-center items-center">
                    <Icon
                      name="play_circle"
                      size="size-x12"
                      color="th-color-neutral-0"
                    />
                  </div>
                </SwiperSlide>
              ))}
        </Swiper>
      </div>
      <div className="flex w-full justify-end gap-size-x4">
        <Text type="paragraphS" color="th-color-neutral-400">
          Aprosoja RS
        </Text>
        <Text type="paragraphS" color="th-color-neutral-400">
          {dayjs(
            mediaGalleryReducer.mediaType === "photos"
              ? photos?.[mediaGalleryReducer.currentSlide]?.createdAt
              : videos?.[mediaGalleryReducer.currentSlide]?.createdAt
          )
            .locale("pt-br")
            .format("DD MMM YYYY")}
        </Text>
      </div>
      <Text type="headingL">
        {mediaGalleryReducer.mediaType === "photos"
          ? photos?.[mediaGalleryReducer.currentSlide]?.title
          : videos?.[mediaGalleryReducer.currentSlide]?.title}
      </Text>
    </div>
  );
};

export default MediaGallery;
